// pages/VideosPageAdmin.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  Chip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import {
  createVideo,
  deleteVideo,
  fetchVideoCategories,
  fetchOnlineStudents,
  getVideos,
  updateVideo,
} from "../utils/generalSubjectApi";
import { getYouTubeId } from "../utils/youtube";

/* ─── Design Tokens ─────────────────────────────────────────── */
const C = {
  indigo: "#6366f1",
  indigoSoft: "rgba(99,102,241,0.08)",
  indigoMid: "rgba(99,102,241,0.15)",
  violet: "#8b5cf6",
  red: "#ef4444",
  redSoft: "rgba(239,68,68,0.08)",
  ink: "#0f172a",
  slate: "#64748b",
  muted: "#94a3b8",
  border: "rgba(15,23,42,0.07)",
  surface: "rgba(255,255,255,0.82)",
  bg: "#f8faff",
};

const glass = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: C.surface,
  border: `1px solid ${C.border}`,
  boxShadow:
    "0 4px 24px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
};

/* ─── Video Card ─────────────────────────────────────────────── */
const VideoAdminCard = ({
  vid,
  index,
  onEdit,
  onDelete,
  navigate,
  categoryName,
}) => {
  const youtubeId = getYouTubeId(vid.video_url);
  const isCloudinary = vid.video_url && !youtubeId;
  const getCloudinaryThumbnail = (url) => {
    if (!url) return null;

    return url
      .replace("/video/upload/", "/video/upload/so_1,q_auto,f_auto/")
      .replace(/\.\w+$/, ".jpg");
  };

  const thumbnail = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
    : isCloudinary
    ? getCloudinaryThumbnail(vid.video_url)
    : null;

  const [imgErr, setImgErr] = useState(false);
  const [hovered, setHovered] = useState(false);
  const teacherName =
    vid.student_name || vid.student?.name || "";

  const year = vid.year || "";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -8 }}
      transition={{
        duration: 0.4,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          ...glass,
          borderRadius: "22px",
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          transition: "box-shadow 0.3s, transform 0.3s",
          ...(hovered && {
            boxShadow:
              "0 20px 56px rgba(99,102,241,0.13), inset 0 1px 0 rgba(255,255,255,0.95)",
            transform: "translateY(-3px)",
          }),
        }}
      >
        {/* Thumbnail */}
        <Box
          onClick={() =>
                navigate(`/videos/${vid.id}`, {
                  state: { subjectId: vid.subject },
                })
              }
          sx={{
            width: { xs: "100%", sm: 210 },
            minHeight: 130,
            flexShrink: 0,
            position: "relative",
            cursor: "pointer",
            overflow: "hidden",
            background: "linear-gradient(135deg,#1e1b4b,#312e81,#4c1d95)",
          }}
        >
          {thumbnail && !imgErr ? (
            <Box
              component="img"
              src={thumbnail}
              alt={vid.title}
              onError={() => setImgErr(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform 0.5s ease",
                transform: hovered ? "scale(1.07)" : "scale(1)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 130,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoLibraryRoundedIcon
                sx={{ color: "rgba(255,255,255,0.2)", fontSize: 44 }}
              />
            </Box>
          )}

          {/* Play Overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.28)",
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.28s",
            }}
          >
            <motion.div
              animate={{ scale: hovered ? 1 : 0.8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                }}
              >
                <PlayArrowRoundedIcon
                  sx={{ color: C.ink, fontSize: 26, ml: "2px" }}
                />
              </Box>
            </motion.div>
          </Box>

          {/* Duration chip placeholder */}
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              background: "rgba(0,0,0,0.62)",
              borderRadius: "6px",
              px: 1,
              py: 0.2,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: "0.68rem",
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
              }}
            >
              YouTube
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            p: "20px 24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {categoryName && (
              <Chip
                label={categoryName}
                size="small"
                icon={
                  <CategoryRoundedIcon
                    style={{ fontSize: 12, color: C.indigo }}
                  />
                }
                sx={{
                  mb: 1.2,
                  height: 22,
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  color: C.indigo,
                  background: C.indigoSoft,
                  border: `1px solid ${C.indigoMid}`,
                  borderRadius: "6px",
                  "& .MuiChip-label": { px: 0.8 },
                  "& .MuiChip-icon": { ml: 0.8 },
                }}
              />
            )}
            <Typography
              onClick={() =>
                navigate(`/videos/${vid.id}`, {
                  state: { subjectId: vid.subject },
                })
              }
              sx={{
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 700,
                fontSize: "0.97rem",
                color: C.ink,
                lineHeight: 1.4,
                letterSpacing: "-0.02em",
                cursor: "pointer",
                mb: 1,
                transition: "color 0.2s",
                "&:hover": { color: C.indigo },
                textTransform: "capitalize",
              }}
            >
              {vid.title}
            </Typography>
            {/* Teacher and Year */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 1.5, flexWrap: "wrap" }}
            >
              {teacherName && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    color: C.slate,
                  }}
                >
                  Teacher: {teacherName}
                </Typography>
              )}

              {year && (
                <Chip
                  label={`Year: ${year}`}
                  size="small"
                  sx={{
                    height: 23,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: C.indigo,
                    background: C.indigoSoft,
                    border: `1px solid ${C.indigoMid}`,
                    borderRadius: "7px",
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <LinkRoundedIcon
                sx={{ fontSize: 12, color: C.muted, flexShrink: 0 }}
              />
              <Typography
                component="a"
                href={vid.video_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: "0.72rem",
                  fontFamily: "'DM Sans',sans-serif",
                  color: C.muted,
                  textDecoration: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 260,
                  transition: "color 0.2s",
                  "&:hover": { color: C.indigo },
                }}
              >
                {vid.video_url}
              </Typography>
            </Stack>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}
            mt={2}
          >
            <Button
             onClick={() =>
                navigate(`/videos/${vid.id}`, {
                  state: { subjectId: vid.subject },
                })
              }
              size="small"
              sx={{
                borderRadius: "10px",
                color: C.indigo,
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 700,
                fontSize: "0.75rem",
                px: 2,
                textTransform: "none",
                "&:hover": { background: C.indigoSoft },
              }}
            >
              Watch
            </Button>

            <motion.div whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.9 }}>
              <IconButton
                onClick={() => onEdit(vid)}
                size="small"
                sx={{
                  color: C.indigo,
                  borderRadius: "10px",
                  width: 34,
                  height: 34,
                  "&:hover": { background: C.indigoSoft },
                }}
              >
                <EditRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </motion.div>

            <motion.div whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.9 }}>
              <IconButton
                onClick={() => onDelete(vid)}
                size="small"
                sx={{
                  color: C.red,
                  borderRadius: "10px",
                  width: 34,
                  height: 34,
                  "&:hover": { background: C.redSoft },
                }}
              >
                <DeleteRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </motion.div>
          </Stack>
        </Box>
      </Box>
    </motion.div>
  );
};

/* ─── Skeleton Card ─────────────────────────────────────────── */
const SkeletonCard = ({ i }) => (
  <motion.div
    key={i}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: i * 0.07 }}
  >
    <Box
      sx={{
        ...glass,
        borderRadius: "22px",
        overflow: "hidden",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: 210,
          minHeight: 130,
          flexShrink: 0,
          background:
            "linear-gradient(90deg, rgba(15,23,42,0.06) 25%, rgba(15,23,42,0.1) 50%, rgba(15,23,42,0.06) 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.6s infinite",
          "@keyframes shimmer": {
            "0%": { backgroundPosition: "200% 0" },
            "100%": { backgroundPosition: "-200% 0" },
          },
        }}
      />
      <Box sx={{ flex: 1, p: "20px 24px" }}>
        {[70, 45].map((w, j) => (
          <Box
            key={j}
            sx={{
              height: j === 0 ? 16 : 12,
              width: `${w}%`,
              borderRadius: 2,
              mb: 1.2,
              background:
                "linear-gradient(90deg, rgba(15,23,42,0.06) 25%, rgba(15,23,42,0.1) 50%, rgba(15,23,42,0.06) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.6s infinite",
            }}
          />
        ))}
      </Box>
    </Box>
  </motion.div>
);

/* ─── Premium Dialog ─────────────────────────────────────────── */
const PremiumDialog = ({
  open,
  onClose,
  title,
  titleColor,
  children,
  actions,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        ...glass,
        borderRadius: "28px",
        overflow: "hidden",
      },
    }}
    slotProps={{
      backdrop: {
        sx: { backdropFilter: "blur(8px)", background: "rgba(15,23,42,0.18)" },
      },
    }}
  >
    <DialogTitle
      sx={{
        fontFamily: "'DM Sans',sans-serif",
        fontWeight: 800,
        fontSize: "1.3rem",
        color: titleColor || C.ink,
        letterSpacing: "-0.03em",
        px: 4,
        pt: 4,
        pb: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {title}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ color: C.muted, borderRadius: "10px" }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ px: 4, pb: 0, pt: 2.5 }}>{children}</DialogContent>
    <DialogActions sx={{ px: 4, pb: 4, pt: 2.5, gap: 1 }}>
      {actions}
    </DialogActions>
  </Dialog>
);

/* ─── StyledField ─────────────────────────────────────────────── */
const Field = (props) => (
  <TextField
    {...props}
    sx={{
      ...(props.sx || {}),
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        fontFamily: "'DM Sans',sans-serif",
        background: "rgba(255,255,255,0.6)",
        ...(props.sx?.["& .MuiOutlinedInput-root"] || {}),
      },
      "& label": { fontFamily: "'DM Sans',sans-serif" },
      "& .MuiMenuItem-root": { fontFamily: "'DM Sans',sans-serif" },
    }}
  />
);

/* ─── Page ──────────────────────────────────────────────────── */
export default function VideosPageAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      setVideos((await getVideos(id)) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };


    
  useEffect(() => {
    fetchOnlineStudents()
      .then(setStudents)
      .catch((error) => {
        console.error("Failed to fetch online students:", error);
      });
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchVideoCategories().then(setCategories);
  }, []);

  const resetForm = () => {
    setTitle("");
    setVideoUrl("");
    setSelectedCategory("");
    setSelectedStudent("");
    setSelectedYear("");
    setEditingId(null);
  };

  const handleEdit = (vid) => {
    setTitle(vid.title);
    setVideoUrl(vid.video_url);
    setSelectedCategory(vid.category || "");
    setSelectedStudent(vid.student || "");
    setEditingId(vid.id);
    setSelectedYear(vid.year || "");
    setFormOpen(true);
  };

  const handleOpenCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !videoUrl.trim()) return;
    const payload = {
      title: title.trim(),
      video_url: videoUrl.trim(),
      subject: id,
      category: selectedCategory || null,
      student: selectedStudent || null,
      year: selectedYear ? Number(selectedYear) : null,
    };
    setSubmitting(true);
    try {
      if (editingId) {
        await updateVideo(editingId, payload);
        setSnackbar({
          open: true,
          message: "Video updated ✓",
          severity: "success",
        });
      } else {
        await createVideo(payload);
        setSnackbar({
          open: true,
          message: "Video added ✓",
          severity: "success",
        });
      }
      setFormOpen(false);
      resetForm();
      fetchData();
    } catch {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteVideo(deleteTarget.id);
      setSnackbar({ open: true, message: "Video deleted", severity: "info" });
      setDeleteTarget(null);
      fetchData();
    } catch {
      setSnackbar({ open: true, message: "Delete failed", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryName = (catId) =>
    categories.find((c) => c.id === catId)?.title || null;

  const filteredVideos = filterCategory
    ? videos.filter((v) => v.category === filterCategory)
    : videos;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse 1000px 700px at 75% -5%, rgba(99,102,241,0.08) 0%, transparent 55%),
                     radial-gradient(ellipse 700px 600px at 0% 100%, rgba(139,92,246,0.06) 0%, transparent 55%),
                     ${C.bg}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient blobs */}
      {[
        { top: -160, right: -120, size: 500, color: "rgba(99,102,241,0.07)" },
        { bottom: -120, left: -100, size: 400, color: "rgba(16,185,129,0.06)" },
      ].map((blob, i) => (
        <Box
          key={i}
          sx={{
            position: "fixed",
            top: blob.top,
            bottom: blob.bottom,
            right: blob.right,
            left: blob.left,
            width: blob.size,
            height: blob.size,
            borderRadius: "50%",
            background: `radial-gradient(circle,${blob.color} 0%,transparent 70%)`,
            filter: "blur(48px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ))}

      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
          px: { xs: 2.5, md: 5 },
          pt: { xs: 4, md: 6 },
          pb: 12,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Back */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 5,
              color: C.slate,
              fontFamily: "'DM Sans',sans-serif",
              fontWeight: 600,
              borderRadius: "12px",
              px: 2.5,
              textTransform: "none",
              background: "rgba(255,255,255,0.8)",
              border: `1px solid ${C.border}`,
              backdropFilter: "blur(12px)",
              fontSize: "0.85rem",
              "&:hover": {
                background: "#fff",
                boxShadow: "0 4px 16px rgba(15,23,42,0.08)",
              },
            }}
          >
            Back
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  color: C.ink,
                  letterSpacing: "-0.05em",
                  lineHeight: 1.05,
                }}
              >
                Video Library
              </Typography>
              <Typography
                sx={{
                  color: C.slate,
                  fontSize: "0.88rem",
                  mt: 0.6,
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {loading
                  ? "Loading…"
                  : `${videos.length} video${
                      videos.length !== 1 ? "s" : ""
                    } in this subject`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/admin-video-categories")}
                  sx={{
                    px: 3,
                    py: 1.3,
                    borderRadius: "14px",
                    fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 700,
                    fontSize: "0.84rem",
                    textTransform: "none",
                    color: C.indigo,
                    borderColor: C.indigoMid,
                    background: C.indigoSoft,
                    "&:hover": {
                      background: "rgba(99,102,241,0.12)",
                      borderColor: C.indigo,
                    },
                  }}
                >
                  Manage Categories
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleOpenCreate}
                  sx={{
                    px: 3.5,
                    py: 1.3,
                    borderRadius: "14px",
                    fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    textTransform: "none",
                    background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                    boxShadow: "0 8px 24px rgba(99,102,241,0.34)",
                    "&:hover": {
                      boxShadow: "0 14px 36px rgba(99,102,241,0.44)",
                      background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                    },
                    transition: "box-shadow 0.25s",
                  }}
                >
                  Add Video
                </Button>
              </motion.div>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/video-stats-page")}
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: "14px",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                View Statistics
              </Button>
            </Stack>
          </Stack>
        </motion.div>

        {/* Divider accent */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "left" }}
        >
          <Box
            sx={{
              height: 2,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${C.indigo}, ${C.violet}, transparent)`,
              mb: 4,
              opacity: 0.3,
            }}
          />
        </motion.div>

        {/* Filter */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            mb={4}
            sx={{ px: 1 }}
          >
            {/* Icon container */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${C.indigoSoft}, ${C.indigoMid})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                transition: "transform 0.25s ease",
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <TuneRoundedIcon sx={{ color: C.indigo, fontSize: 20 }} />
            </Box>

            {/* Category filter */}
            <Field
              select
              label="Filter by Category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              size="small"
              sx={{
                minWidth: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 500,
                },
              }}
            >
              <MenuItem value="" sx={{ fontFamily: "'DM Sans', sans-serif" }}>
                All Categories
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem
                  key={cat.id}
                  value={cat.id}
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    textTransform: "capitalize",
                    fontWeight: 500,
                  }}
                >
                  {cat.title}
                </MenuItem>
              ))}
            </Field>

            {/* Results chip */}
            {filterCategory && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <Chip
                  label={`${filteredVideos.length} result${
                    filteredVideos.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  onDelete={() => setFilterCategory("")}
                  deleteIcon={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: C.indigo,
                    background: C.indigoSoft,
                    border: `1px solid ${C.indigoMid}`,
                    borderRadius: "10px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: C.indigoMid,
                      color: "#fff",
                    },
                  }}
                />
              </motion.div>
            )}
          </Stack>
        </motion.div>

        {/* Video List */}
        <Stack spacing={2.5}>
          <AnimatePresence mode="popLayout">
            {loading ? (
              [...Array(3)].map((_, i) => <SkeletonCard key={i} i={i} />)
            ) : filteredVideos.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <Box
                  sx={{
                    ...glass,
                    borderRadius: "28px",
                    textAlign: "center",
                    py: 10,
                    px: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "20px",
                      background: C.indigoSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <VideoLibraryRoundedIcon
                      sx={{ color: C.indigo, fontSize: 34 }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontWeight: 800,
                      fontSize: "1.4rem",
                      color: C.ink,
                      mb: 1,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {filterCategory ? "No matching videos" : "No videos yet"}
                  </Typography>
                  <Typography
                    sx={{ color: C.slate, fontSize: "0.88rem", mb: 4 }}
                  >
                    {filterCategory
                      ? "Try a different category filter"
                      : "Add your first video to get started"}
                  </Typography>
                  {!filterCategory && (
                    <Button
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={handleOpenCreate}
                      sx={{
                        px: 5,
                        py: 1.5,
                        borderRadius: "14px",
                        fontFamily: "'DM Sans',sans-serif",
                        fontWeight: 700,
                        textTransform: "none",
                        background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                        boxShadow: "0 8px 24px rgba(99,102,241,0.32)",
                      }}
                    >
                      Add Video
                    </Button>
                  )}
                </Box>
              </motion.div>
            ) : (
              filteredVideos.map((vid, i) => (
                <VideoAdminCard
                  key={vid.id}
                  vid={vid}
                  index={i}
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                  navigate={navigate}
                  categoryName={getCategoryName(vid.category)}
                />
              ))
            )}
          </AnimatePresence>
        </Stack>
      </Box>

      {/* ── FORM DIALOG ── */}
      <PremiumDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          resetForm();
        }}
        title={editingId ? "Edit Video" : "Add Video"}
        actions={
          <>
            <Button
              onClick={() => {
                setFormOpen(false);
                resetForm();
              }}
              disabled={submitting}
              sx={{
                color: C.slate,
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!title.trim() || !videoUrl.trim() || submitting}
                sx={{
                  px: 5,
                  py: 1.3,
                  borderRadius: "12px",
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 700,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                  boxShadow: "0 6px 20px rgba(99,102,241,0.32)",
                  minWidth: 120,
                }}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : editingId ? (
                  "Save Changes"
                ) : (
                  "Add Video"
                )}
              </Button>
            </motion.div>
          </>
        }
      >
        <Stack spacing={2.5}>
          <Field
            label="Video Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            autoFocus
          />
          <Field
            label="YouTube URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            fullWidth
            placeholder="https://www.youtube.com/watch?v=…"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
          <Field
            select
            label="Category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            fullWidth
          >
            <MenuItem value="" sx={{ fontFamily: "'DM Sans',sans-serif" }}>
              <em>No Category</em>
            </MenuItem>
            {categories.map((cat) => (
              <MenuItem
                key={cat.id}
                value={cat.id}
                sx={{
                  fontFamily: "'DM Sans',sans-serif",
                  textTransform: "capitalize",
                }}
              >
                {cat.title}
              </MenuItem>
            ))}
          </Field>

          <Field
            select
            label="Online Teacher(optional)"
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            fullWidth
          >
            <MenuItem
              value=""
              sx={{ fontFamily: "'DM Sans',sans-serif" }}
            >
              <em>No teacher</em>
            </MenuItem>

            {students.map((student) => (
              <MenuItem
                key={student.id}
                value={student.id}
                sx={{
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {student.name}
              </MenuItem>
            ))}
          </Field>
          <Field
            label="Year(optional)"
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            fullWidth
            placeholder="e.g. 2024"
          />
        </Stack>
      </PremiumDialog>

      {/* ── DELETE DIALOG ── */}
      <PremiumDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Video?"
        titleColor={C.red}
        actions={
          <>
            <Button
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              sx={{
                color: C.slate,
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                onClick={handleDelete}
                disabled={deleting}
                sx={{
                  px: 5,
                  py: 1.3,
                  borderRadius: "12px",
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(135deg,#ef4444,#dc2626)",
                  boxShadow: "0 6px 20px rgba(239,68,68,0.28)",
                  minWidth: 110,
                }}
              >
                {deleting ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Delete"
                )}
              </Button>
            </motion.div>
          </>
        }
      >
        <Box
          sx={{
            background: C.redSoft,
            borderRadius: "14px",
            px: 2.5,
            py: 2,
            border: "1px solid rgba(239,68,68,0.14)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              color: "#b91c1c",
              fontSize: "0.88rem",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            <strong>"{deleteTarget?.title}"</strong> will be permanently
            removed. This action cannot be undone.
          </Typography>
        </Box>
      </PremiumDialog>

      {/* ── SNACKBAR ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{
            borderRadius: "14px",
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: 600,
            boxShadow: "0 8px 28px rgba(15,23,42,0.16)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
